// @ts-nocheck
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  User, Image, Building2, Users, Edit, Loader2, Upload, Plus, Clock, CheckCircle2, XCircle, TreeDeciduous, LogOut, Camera, Video
} from "lucide-react";
import defaultMale from "@/assets/default-male.png";
import defaultFemale from "@/assets/default-female.png";

type Tab = "profile" | "photos" | "business" | "children" | "requests";

const MemberDashboard = () => {
  const { user, isMember, claimStatus, isLoading, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>("profile");

  useEffect(() => {
    if (!isLoading && !user) navigate("/login");
    if (!isLoading && user && isAdmin) navigate("/admin");
    if (!isLoading && user && (!isMember || claimStatus !== "approved")) navigate("/claim");
  }, [user, isAdmin, isMember, claimStatus, isLoading, navigate]);

  // Get linked member data
  const { data: memberData } = useQuery({
    queryKey: ["my-member-data", user?.id],
    queryFn: async () => {
      if (!user) return null;
      // Get claim to find member_id
      const { data: claim } = await supabase
        .from("member_claims")
        .select("member_id")
        .eq("user_id", user.id)
        .eq("status", "approved")
        .maybeSingle();
      if (!claim?.member_id) return null;

      const { data: member } = await supabase
        .from("family_members")
        .select("*")
        .eq("id", claim.member_id)
        .single();
      return member;
    },
    enabled: !!user && isMember,
  });

  // Get my requests
  const { data: myRequests } = useQuery({
    queryKey: ["my-requests", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("member_requests")
        .select("*")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      return data || [];
    },
    enabled: !!user,
  });

  // Get my children
  const { data: myChildren } = useQuery({
    queryKey: ["my-children", memberData?.id],
    queryFn: async () => {
      if (!memberData) return [];
      const { data: rels } = await supabase
        .from("family_relationships")
        .select("child_id")
        .eq("parent_id", memberData.id);
      if (!rels?.length) return [];
      const childIds = rels.map(r => r.child_id);
      const { data: children } = await supabase
        .from("family_members")
        .select("*")
        .in("id", childIds);
      return children || [];
    },
    enabled: !!memberData,
  });

  if (isLoading || !memberData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: "profile", label: "ملفي الشخصي", icon: <User className="h-4 w-4" /> },
    { id: "photos", label: "صور وفيديو", icon: <Camera className="h-4 w-4" /> },
    { id: "business", label: "شركتي", icon: <Building2 className="h-4 w-4" /> },
    { id: "children", label: "أبنائي", icon: <Users className="h-4 w-4" /> },
    { id: "requests", label: "طلباتي", icon: <Clock className="h-4 w-4" /> },
  ];

  const pendingCount = myRequests?.filter(r => r.status === "pending").length || 0;

  return (
    <div className="min-h-screen px-4 py-8 pt-24 max-w-4xl mx-auto">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-primary/30">
              <img
                src={memberData.photo_url || (memberData.gender === "female" ? defaultFemale : defaultMale)}
                alt={memberData.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h1 className="font-amiri text-2xl md:text-3xl font-bold gold-text">مرحباً، {memberData.name}</h1>
              <p className="text-sm text-muted-foreground">لوحة التحكم الخاصة بك</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-xl" onClick={() => navigate("/tree")}>
              <TreeDeciduous className="h-4 w-4 ml-1" />
              الشجرة
            </Button>
            <Button variant="ghost" size="sm" className="rounded-xl" onClick={signOut}>
              <LogOut className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide">
        {tabs.map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? "default" : "outline"}
            size="sm"
            className={`rounded-xl whitespace-nowrap ${activeTab === tab.id ? "gold-gradient text-primary-foreground" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            <span className="mr-1">{tab.label}</span>
            {tab.id === "requests" && pendingCount > 0 && (
              <Badge variant="secondary" className="mr-1 h-5 w-5 p-0 flex items-center justify-center text-[10px]">{pendingCount}</Badge>
            )}
          </Button>
        ))}
      </div>

      {/* Tab Content */}
      <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        {activeTab === "profile" && <ProfileTab member={memberData} toast={toast} queryClient={queryClient} />}
        {activeTab === "photos" && <PhotosTab memberId={memberData.id} userId={user!.id} toast={toast} queryClient={queryClient} />}
        {activeTab === "business" && <BusinessTab memberId={memberData.id} userId={user!.id} toast={toast} queryClient={queryClient} />}
        {activeTab === "children" && <ChildrenTab member={memberData} children={myChildren || []} userId={user!.id} toast={toast} queryClient={queryClient} />}
        {activeTab === "requests" && <RequestsTab requests={myRequests || []} />}
      </motion.div>
    </div>
  );
};

// =================== PROFILE TAB ===================
function ProfileTab({ member, toast, queryClient }: any) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    phone: member.phone || "",
    location: member.location || "",
    work: member.work || "",
    bio: member.bio || "",
    marital_status: member.marital_status || "unknown",
  });

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("family_members")
      .update({
        phone: form.phone || null,
        location: form.location || null,
        work: form.work || null,
        bio: form.bio || null,
        marital_status: form.marital_status,
      })
      .eq("id", member.id);
    setSaving(false);

    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "تم تحديث بياناتك بنجاح" });
      queryClient.invalidateQueries({ queryKey: ["my-member-data"] });
      setEditing(false);
    }
  };

  return (
    <Card className="glass-card border-primary/10 rounded-2xl">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-amiri text-xl gold-text">بياناتي الشخصية</CardTitle>
          {!editing && (
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
              <Edit className="h-4 w-4 ml-1" />
              تعديل
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-muted-foreground text-xs">الاسم</Label>
            <p className="text-foreground font-medium">{member.name}</p>
          </div>
          <div>
            <Label className="text-muted-foreground text-xs">البريد الإلكتروني</Label>
            <p className="text-foreground font-medium" dir="ltr">{member.email || "—"}</p>
            <p className="text-[10px] text-muted-foreground">البريد مرتبط بحسابك ولا يمكن تعديله</p>
          </div>
          <div>
            <Label className="text-muted-foreground text-xs">الهاتف</Label>
            {editing ? (
              <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} dir="ltr" className="bg-secondary/50 border-primary/10 rounded-xl" />
            ) : (
              <p className="text-foreground" dir="ltr">{member.phone || "—"}</p>
            )}
          </div>
          <div>
            <Label className="text-muted-foreground text-xs">الموقع</Label>
            {editing ? (
              <Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} className="bg-secondary/50 border-primary/10 rounded-xl" />
            ) : (
              <p className="text-foreground">{member.location || "—"}</p>
            )}
          </div>
          <div>
            <Label className="text-muted-foreground text-xs">العمل</Label>
            {editing ? (
              <Input value={form.work} onChange={e => setForm(f => ({ ...f, work: e.target.value }))} className="bg-secondary/50 border-primary/10 rounded-xl" />
            ) : (
              <p className="text-foreground">{member.work || "—"}</p>
            )}
          </div>
          <div>
            <Label className="text-muted-foreground text-xs">الحالة الاجتماعية</Label>
            {editing ? (
              <Select value={form.marital_status} onValueChange={v => setForm(f => ({ ...f, marital_status: v }))}>
                <SelectTrigger className="bg-secondary/50 border-primary/10 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unknown">غير محدد</SelectItem>
                  <SelectItem value="single">أعزب/عزباء</SelectItem>
                  <SelectItem value="married">متزوج/ة</SelectItem>
                  <SelectItem value="divorced">مطلق/ة</SelectItem>
                  <SelectItem value="widowed">أرمل/ة</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <p className="text-foreground">
                {({ unknown: "غير محدد", single: "أعزب/عزباء", married: "متزوج/ة", divorced: "مطلق/ة", widowed: "أرمل/ة" } as any)[member.marital_status] || "غير محدد"}
              </p>
            )}
          </div>
        </div>
        {editing && (
          <div>
            <Label className="text-muted-foreground text-xs">نبذة</Label>
            <Textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} className="bg-secondary/50 border-primary/10 rounded-xl" rows={3} />
          </div>
        )}
        {editing && (
          <div className="flex gap-2 pt-2">
            <Button className="gold-gradient text-primary-foreground rounded-xl" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin ml-1" />}
              حفظ التعديلات
            </Button>
            <Button variant="outline" className="rounded-xl" onClick={() => setEditing(false)}>إلغاء</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =================== PHOTOS TAB ===================
function PhotosTab({ memberId, userId, toast, queryClient }: any) {
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");
    if (!isImage && !isVideo) {
      toast({ title: "نوع الملف غير مدعوم", variant: "destructive" });
      return;
    }

    setUploading(true);

    // Upload file
    const bucket = isVideo ? "news-videos" : "gallery-photos";
    const ext = file.name.split(".").pop();
    const filePath = `member-uploads/${memberId}/${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage.from(bucket).upload(filePath, file);
    if (uploadError) {
      toast({ title: "فشل رفع الملف", description: uploadError.message, variant: "destructive" });
      setUploading(false);
      return;
    }

    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(filePath);

    // Submit request
    const { error } = await supabase.from("member_requests").insert({
      user_id: userId,
      member_id: memberId,
      request_type: isVideo ? "video" : "photo",
      data: {
        url: urlData.publicUrl,
        title: title || null,
        description: description || null,
        file_type: isVideo ? "video" : "image",
      },
    });

    setUploading(false);
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "تم إرسال الطلب", description: "سيتم مراجعة الصورة/الفيديو من قبل المشرف" });
      setTitle("");
      setDescription("");
      queryClient.invalidateQueries({ queryKey: ["my-requests"] });
    }
  };

  return (
    <Card className="glass-card border-primary/10 rounded-2xl">
      <CardHeader>
        <CardTitle className="font-amiri text-xl gold-text flex items-center gap-2">
          <Camera className="h-5 w-5 text-primary" />
          إضافة صور وفيديوهات
        </CardTitle>
        <p className="text-sm text-muted-foreground">ارفع صورة أو فيديو وسيتم مراجعته من قبل المشرف قبل الإضافة</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>عنوان (اختياري)</Label>
          <Input value={title} onChange={e => setTitle(e.target.value)} className="bg-secondary/50 border-primary/10 rounded-xl" placeholder="مثل: صورة عائلية" />
        </div>
        <div>
          <Label>وصف (اختياري)</Label>
          <Textarea value={description} onChange={e => setDescription(e.target.value)} className="bg-secondary/50 border-primary/10 rounded-xl" rows={2} />
        </div>
        <div>
          <Label htmlFor="photo-upload" className="cursor-pointer">
            <div className="border-2 border-dashed border-primary/20 rounded-xl p-8 text-center hover:border-primary/40 transition-colors">
              {uploading ? (
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
              ) : (
                <>
                  <Upload className="h-8 w-8 text-primary mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">اضغط لاختيار صورة أو فيديو</p>
                  <p className="text-xs text-muted-foreground mt-1">JPG, PNG, MP4, MOV</p>
                </>
              )}
            </div>
          </Label>
          <input id="photo-upload" type="file" accept="image/*,video/*" className="hidden" onChange={handleUpload} disabled={uploading} />
        </div>
      </CardContent>
    </Card>
  );
}

// =================== BUSINESS TAB ===================
function BusinessTab({ memberId, userId, toast, queryClient }: any) {
  const [form, setForm] = useState({
    name_ar: "",
    name_en: "",
    activity: "",
    phone: "",
    email: "",
    website: "",
    country: "الإمارات العربية المتحدة",
    emirate: "",
    area: "",
    street: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!form.name_ar.trim()) {
      toast({ title: "أدخل اسم الشركة", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("member_requests").insert({
      user_id: userId,
      member_id: memberId,
      request_type: "business",
      data: form,
    });
    setSubmitting(false);
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "تم إرسال الطلب", description: "سيتم مراجعة بيانات الشركة من قبل المشرف" });
      setForm({ name_ar: "", name_en: "", activity: "", phone: "", email: "", website: "", country: "الإمارات العربية المتحدة", emirate: "", area: "", street: "" });
      queryClient.invalidateQueries({ queryKey: ["my-requests"] });
    }
  };

  return (
    <Card className="glass-card border-primary/10 rounded-2xl">
      <CardHeader>
        <CardTitle className="font-amiri text-xl gold-text flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          إضافة شركة خاصة بي
        </CardTitle>
        <p className="text-sm text-muted-foreground">أضف بيانات شركتك وسيتم مراجعتها من قبل المشرف</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>اسم الشركة بالعربية *</Label>
            <Input value={form.name_ar} onChange={e => setForm(f => ({ ...f, name_ar: e.target.value }))} className="bg-secondary/50 border-primary/10 rounded-xl" />
          </div>
          <div>
            <Label>اسم الشركة بالإنجليزية</Label>
            <Input value={form.name_en} onChange={e => setForm(f => ({ ...f, name_en: e.target.value }))} className="bg-secondary/50 border-primary/10 rounded-xl" dir="ltr" />
          </div>
          <div>
            <Label>النشاط</Label>
            <Input value={form.activity} onChange={e => setForm(f => ({ ...f, activity: e.target.value }))} className="bg-secondary/50 border-primary/10 rounded-xl" />
          </div>
          <div>
            <Label>الهاتف</Label>
            <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="bg-secondary/50 border-primary/10 rounded-xl" dir="ltr" />
          </div>
          <div>
            <Label>البريد الإلكتروني</Label>
            <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="bg-secondary/50 border-primary/10 rounded-xl" dir="ltr" />
          </div>
          <div>
            <Label>الموقع الإلكتروني</Label>
            <Input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} className="bg-secondary/50 border-primary/10 rounded-xl" dir="ltr" />
          </div>
          <div>
            <Label>الإمارة</Label>
            <Input value={form.emirate} onChange={e => setForm(f => ({ ...f, emirate: e.target.value }))} className="bg-secondary/50 border-primary/10 rounded-xl" />
          </div>
          <div>
            <Label>المنطقة</Label>
            <Input value={form.area} onChange={e => setForm(f => ({ ...f, area: e.target.value }))} className="bg-secondary/50 border-primary/10 rounded-xl" />
          </div>
        </div>
        <Button className="gold-gradient text-primary-foreground rounded-xl" onClick={handleSubmit} disabled={submitting}>
          {submitting && <Loader2 className="h-4 w-4 animate-spin ml-1" />}
          إرسال للمراجعة
        </Button>
      </CardContent>
    </Card>
  );
}

// =================== CHILDREN TAB ===================
function ChildrenTab({ member, children, userId, toast, queryClient }: any) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    gender: "male",
    birth_date: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast({ title: "أدخل اسم الابن/البنت", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("member_requests").insert({
      user_id: userId,
      member_id: member.id,
      request_type: "child",
      data: {
        name: form.name,
        gender: form.gender,
        birth_date: form.birth_date || null,
        parent_name: member.name,
      },
    });
    setSubmitting(false);
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "تم إرسال الطلب", description: "سيتم مراجعة الطلب من قبل المشرف" });
      setForm({ name: "", gender: "male", birth_date: "" });
      setShowForm(false);
      queryClient.invalidateQueries({ queryKey: ["my-requests"] });
    }
  };

  return (
    <div className="space-y-4">
      {/* Existing children */}
      <Card className="glass-card border-primary/10 rounded-2xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-amiri text-xl gold-text flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              أبنائي
            </CardTitle>
            <Button size="sm" className="gold-gradient text-primary-foreground rounded-xl" onClick={() => setShowForm(!showForm)}>
              <Plus className="h-4 w-4 ml-1" />
              إضافة ابن/بنت
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {children.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">لا يوجد أبناء مسجلين حالياً</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {children.map((child: any) => (
                <div key={child.id} className="flex items-center gap-3 p-3 rounded-xl bg-secondary/30 border border-primary/5">
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-primary/20">
                    <img
                      src={child.photo_url || (child.gender === "female" ? defaultFemale : defaultMale)}
                      alt={child.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{child.name}</p>
                    <p className="text-xs text-muted-foreground">{child.gender === "female" ? "بنت" : "ابن"}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add child form */}
      {showForm && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="glass-card border-primary/10 rounded-2xl">
            <CardHeader>
              <CardTitle className="font-amiri text-lg gold-text">إضافة ابن/بنت جديد</CardTitle>
              <p className="text-sm text-muted-foreground">سيتم مراجعة الطلب من قبل المشرف قبل الإضافة</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>الاسم الكامل *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-secondary/50 border-primary/10 rounded-xl" />
              </div>
              <div>
                <Label>الجنس</Label>
                <Select value={form.gender} onValueChange={v => setForm(f => ({ ...f, gender: v }))}>
                  <SelectTrigger className="bg-secondary/50 border-primary/10 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">ذكر</SelectItem>
                    <SelectItem value="female">أنثى</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>تاريخ الميلاد</Label>
                <Input type="date" value={form.birth_date} onChange={e => setForm(f => ({ ...f, birth_date: e.target.value }))} className="bg-secondary/50 border-primary/10 rounded-xl" dir="ltr" />
              </div>
              <div className="flex gap-2">
                <Button className="gold-gradient text-primary-foreground rounded-xl" onClick={handleSubmit} disabled={submitting}>
                  {submitting && <Loader2 className="h-4 w-4 animate-spin ml-1" />}
                  إرسال للمراجعة
                </Button>
                <Button variant="outline" className="rounded-xl" onClick={() => setShowForm(false)}>إلغاء</Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}

// =================== REQUESTS TAB ===================
function RequestsTab({ requests }: { requests: any[] }) {
  const typeLabels: Record<string, string> = {
    photo: "صورة",
    video: "فيديو",
    business: "شركة",
    child: "إضافة ابن/بنت",
    edit_profile: "تعديل الملف الشخصي",
    edit_child: "تعديل بيانات ابن",
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary" className="text-amber-500 bg-amber-500/10"><Clock className="h-3 w-3 ml-1" />قيد المراجعة</Badge>;
      case "approved":
        return <Badge variant="secondary" className="text-green-500 bg-green-500/10"><CheckCircle2 className="h-3 w-3 ml-1" />تمت الموافقة</Badge>;
      case "rejected":
        return <Badge variant="secondary" className="text-destructive bg-destructive/10"><XCircle className="h-3 w-3 ml-1" />مرفوض</Badge>;
      default:
        return null;
    }
  };

  return (
    <Card className="glass-card border-primary/10 rounded-2xl">
      <CardHeader>
        <CardTitle className="font-amiri text-xl gold-text">طلباتي</CardTitle>
      </CardHeader>
      <CardContent>
        {requests.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">لا توجد طلبات بعد</p>
        ) : (
          <div className="space-y-3">
            {requests.map(req => (
              <div key={req.id} className="flex items-center justify-between p-3 rounded-xl bg-secondary/30 border border-primary/5">
                <div>
                  <p className="text-sm font-medium text-foreground">{typeLabels[req.request_type] || req.request_type}</p>
                  <p className="text-xs text-muted-foreground">{new Date(req.created_at).toLocaleDateString("ar-SA")}</p>
                  {req.admin_note && <p className="text-xs text-muted-foreground mt-1">ملاحظة: {req.admin_note}</p>}
                </div>
                {statusBadge(req.status)}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default MemberDashboard;
